import { useForm, useWatch } from "react-hook-form";
import { ADDRESS, ADDRESS_1, ALTERNATE_LOGIN_ID, CITY, COMMUNITY, CORRESPONDENCE_ADDRESS, CUSTOM_ADDRESS, EMAIL_ID_1, EMAIL_ID_2, END_DATE, EXISTING, FIRST_NAME, INDIVIDUAL, LANDLINE_NO, LANDLORD, LAST_NAME, MOBILE_NO, MULTI_CORRESPOND, NEW_USER, OCCUPANCY_TYPE, ORGANIZATION_NAME, orgOwnershipType, OWNER, OWNERSHIP_SHARE, OWNERSHIP_TYPE, PO_BOX, PRIMARY_USER, PROPERTY, START_DATE, STATE, USER_ID, USER_TYPE, USERNAME, USERS, ZIPCODE } from "./consts";
import { useAddOCPClosingDocumentMutation, useDeleteOCPClosingDocumentMutation, useGetClosingProofList, useGetCommunities, useGetOCPDocumentList, useGetPropertiesByCommunity, useGetPropertyOwnerByIdV2, useGetState, useUpdateOCPClosingDocumentMutation } from "@/services";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { getRequiredFields } from "@/lib/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { AddressType } from "./types";
import { useLocation, useParams } from "react-router-dom";
import moment from "moment";
import { ATTACHMENT_ID, FILE_SIZE, FILE_TYPE, IS_REQUIRED, OCP_ID, OWNERSHIP_LIST, PACKET_DOC_ID, PROOF_DOCS, PROOF_FILE, PROOF_TYPE, proofTypeOptions, PROPERTY_ID, STATUS, UPLOADED_STATUS } from "../../consts";
import { getFormSchema } from "./validations";
import { toast } from "@/components/Toast";
import { setOCPData } from "@/redux/slices";

const useStep5 = ({onSubmitBaseForm}: {onSubmitBaseForm: any}) => {
  const { action } = useParams();
  const isView = action == "view";
  const isEdit = action == "edit";
  const isMobile = useIsMobile();
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const ocpData = useSelector((state: RootState) => state.currentData.ocpData);
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const path = useLocation().pathname?.split("/");
  const [updatedKey, setUpdatedKey] = useState<number>(Math.random() * 2);
  const [closingKey, setClosingKey] = useState<number>(Math.random() * 3);
  const dispatch = useDispatch();
  const deletePacketDocId = useRef<string[]>([]);

  const form = useForm<any>({
    defaultValues: {
      [COMMUNITY]: communityData?.communityId?.toString(),
      [PROPERTY]: ocpData?.[PROPERTY_ID]?.toString(),
      [OWNERSHIP_TYPE]: INDIVIDUAL,
      [MULTI_CORRESPOND]: false,
      [CORRESPONDENCE_ADDRESS]: "property",
      [ORGANIZATION_NAME]: "",
      [USERS]: [],
      [CUSTOM_ADDRESS]: null,
      [PROOF_DOCS]: [{
        [ATTACHMENT_ID]: "",
        [PROOF_TYPE]: "",
        [PROOF_FILE]: null
      }]
    },
  });

  const [openAddEditDialog, setOpenAddEditDialog] = useState<boolean>(false);
  const [openRemoveOwnerDialog, setOpenRemoveOwnerDialog] = useState<boolean>(false);
  const [openDeleteSingleOwnerDialog, setOpenDeleteSingleOwnerDialog] = useState<boolean>(false);
  const [keys, setKeys] = useState({
    state: Math.random() * 2
  });

  const dialogAction = useRef("add");
  const selectedUser = useRef<any>(null);

  const requiredFields = getRequiredFields(getFormSchema(isEdit));
  const formData = useWatch({ control: form.control });

  const communityId = useWatch({ control: form.control, name: COMMUNITY});

  const { data: communityList, isLoading: isCommunityLoading } =
    useGetCommunities({});

  const { data: propertyList, isLoading: isPropertyLoading } =
    useGetPropertiesByCommunity({
      communityId: communityId as string,
      queryParams: {
        enabled: !!communityId
      }
    });

  const ownershipId = useMemo(() => ocpData?.newOwnershipId, [ocpData]);

  const { data: ownershipDtl, isLoading: isOwnershipDtlLoading } =
    useGetPropertyOwnerByIdV2({
      communityId: communityData?.communityId,
      id: ownershipId as string,
      queryParams: {
        enabled: !!(path[path.length - 1] != "add" && ownershipId)
      }
    });

  const { data: stateList, isLoading: isStateLoading } = useGetState({});
  
  useEffect(() => {
    if (ownershipDtl) {
      const data = ownershipDtl?.data;
      form.reset({
        ...formData,
        [COMMUNITY]: communityData?.communityId?.toString(),
        [PROPERTY]: ocpData?.[PROPERTY_ID]?.toString() || "",
        [OWNERSHIP_TYPE]: data?.[OWNERSHIP_TYPE] || INDIVIDUAL,
        [MULTI_CORRESPOND]: data?.[MULTI_CORRESPOND] || false,
        [CORRESPONDENCE_ADDRESS]: data?.[CORRESPONDENCE_ADDRESS] || "property",
        [CUSTOM_ADDRESS]: data?.[CUSTOM_ADDRESS] || null,
        [USERS]: data?.[USERS]?.map((usr: any) => ({
          ...usr,
          [USER_ID]: usr?.[USER_ID]?.toString(),
          [ALTERNATE_LOGIN_ID]: usr?.[ALTERNATE_LOGIN_ID]?.toString(),
          [OWNERSHIP_SHARE]: usr?.[OWNERSHIP_SHARE]?.toString(),
          [USER_TYPE]: EXISTING,
          [START_DATE]: usr?.[START_DATE] ? usr?.[START_DATE]?.toString() : "",
          [END_DATE]: usr?.[END_DATE] ? usr?.[END_DATE]?.toString() : ""
        }))
      })
      setKeys({
        state: Math.random() * 3
      })
    }
  }, [ownershipDtl]);

  const onOwnershipTypeChange = (type: any) => () => {
    form.setValue(OWNERSHIP_TYPE, type);
  };

  const { data: ocpDocList, isLoading: isDocListLoading } = useGetOCPDocumentList({
    communityId: communityData?.communityId,
    ocpStage: "OWNER_ONBOARDING",
    queryParams: { enabled: !!communityData?.communityId },
  });
  
  const { data: closingProofList, isLoading: isLoadingProofList } = useGetClosingProofList({
    ocpId: ocpData?.[OCP_ID],
    ocpStage: "OWNER_ONBOARDING",
    queryParams: {
      enabled: !!ocpData?.[OCP_ID]
    }
  });

  useEffect(() => {
    if (ocpDocList?.data) {
      const list = ocpDocList?.data?.filter((data: any) => data?.requirementType == "REQUIRED")?.map((data: any) => {
        const closingDoc = closingProofList?.data?.documents?.find((val: any) => val?.documentTypeId == data?.documentTypeId);
        return {
          [ATTACHMENT_ID]: closingDoc?.[ATTACHMENT_ID]?.toString() || "",
          [PACKET_DOC_ID]: closingDoc?.[PACKET_DOC_ID]?.toString(),
          [PROOF_TYPE]: data?.documentTypeId?.toString(),
          [PROOF_FILE]: closingDoc?.fileName ? {name: closingDoc?.fileName, size: closingDoc?.fileSize, url: closingDoc?.presignedUrl} : null,
          [IS_REQUIRED]: data?.requirementType == "REQUIRED",
          [FILE_SIZE]: data[FILE_SIZE]?.toString() || "",
          [FILE_TYPE]: data?.[FILE_TYPE]?.length ? data?.[FILE_TYPE]?.map((val: any) => `.${val}`)?.join(",") : "",
          [UPLOADED_STATUS]: closingDoc?.[UPLOADED_STATUS] || false
        }
      });

      form.setValue(PROOF_DOCS, list?.length ? list : [{
        [PACKET_DOC_ID]: "",
        [ATTACHMENT_ID]: "",
        [PROOF_TYPE]: "",
        [IS_REQUIRED]: false,
        [FILE_SIZE]: "",
        [FILE_TYPE]: "",
        [PROOF_FILE]: null,
        [UPLOADED_STATUS]: false
      }])
      setUpdatedKey(Math.random() * 3);
    }
  }, [ocpDocList, closingProofList]);
  
  const onAddUser = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    dialogAction.current = "add";
    selectedUser.current = null;
    setOpenAddEditDialog(true);
  }

  const onSave = (data: any) => {
    let users = [...(formData as any)[USERS]];
    if (data.rowIndex) {
      users[data.rowIndex] = {...data};
    } else {
      users = [...users, data];
    }

    const isPrimaryUserExist = users?.some((data: any) => data[OCCUPANCY_TYPE] == OWNER && data[PRIMARY_USER]);
 
    if (!isPrimaryUserExist) {
      const firstOwnerIdx = users?.findIndex((data: any) => data[OCCUPANCY_TYPE] == OWNER);
      users[firstOwnerIdx] = {...users[firstOwnerIdx], [PRIMARY_USER]: true};
    }

    form.setValue(USERS, [...users]);
    setOpenAddEditDialog(false);
  }

  const onRemoveSave = (data: any) => {
    let users = [...(formData as any)[USERS]];
    const deletedIdx = users.findIndex((usr: any) => usr.userId == selectedUser.current.userId);
    users[deletedIdx] = {...users[deletedIdx], delete: true};
    form.setValue(USERS, [...users, ...data]);
    setOpenRemoveOwnerDialog(false);
  }

  const onEditUser = (user: any, idx: number) => {
    dialogAction.current = "edit";
    selectedUser.current = {...user, rowIndex: idx?.toString()};
    setOpenAddEditDialog(true);
  }

  const onAddressTypeChange = (value: AddressType) => {
    form.setValue(CORRESPONDENCE_ADDRESS, value)
  }
  
  const addUserDisabled = useMemo(() => {
    return formData[OWNERSHIP_TYPE] == INDIVIDUAL && formData[USERS]?.length == 1 ? true : false
  }, [formData]);

  const validateCustomAddress = (address: any) => {
    const requiredFields = [
      { key: ADDRESS_1, name: ADDRESS_1 },
      { key: STATE, name: STATE },
      { key: CITY, name: CITY },
      { key: ZIPCODE, name: ZIPCODE },
      { key: PO_BOX, name: PO_BOX },
    ];
  
    let isValid = true;
  
    if (formData[CORRESPONDENCE_ADDRESS] == "custom") {
      requiredFields.forEach(({ key, name }) => {
        if (!address?.[key]?.trim?.() && !address?.[key]) {
          form.setError(`${CUSTOM_ADDRESS}.${name}` as any, {
            type: "manual",
            message: "This Field is required",
          });
          isValid = false;
        }
      });
    }
  
    return isValid;
  }

  const baseFormSubmit = (data: any) => {
    let finalObj = []
      
    if (validateCustomAddress(data[CUSTOM_ADDRESS])) {
      if (isEdit) {
        finalObj = data?.[USERS]?.map((usr: any) => {
          const obj = {
            [OCCUPANCY_TYPE]: usr[OCCUPANCY_TYPE],
            [PRIMARY_USER]: usr[PRIMARY_USER],
            [START_DATE]: usr[START_DATE] ? moment(usr[START_DATE]).format('YYYY-MM-DDT00:00:00') : null,
            [END_DATE]: usr[END_DATE] ? moment(usr[END_DATE]).format('YYYY-MM-DDT23:59:59') : null,
            [OWNERSHIP_SHARE]: usr[OCCUPANCY_TYPE] == OWNER ? usr[OWNERSHIP_SHARE] : ""
          }
  
          if (usr.userId) {
            return {
              [USER_ID]: usr[USER_ID],
              ...obj
            }
          } else {
            return {
              [NEW_USER]: {
                [USERNAME]: usr[USERNAME],
                [ALTERNATE_LOGIN_ID]: usr[ALTERNATE_LOGIN_ID],
                "title": "",
                [FIRST_NAME]: usr[FIRST_NAME],
                [LAST_NAME]: usr[LAST_NAME],
                [MOBILE_NO]: usr[MOBILE_NO],
                [LANDLINE_NO]: usr[LANDLINE_NO],
                [EMAIL_ID_1]: usr[EMAIL_ID_1],
                [EMAIL_ID_2]: usr[EMAIL_ID_2],
                [ADDRESS]: usr[ADDRESS],
                [PRIMARY_USER]: usr[PRIMARY_USER],
              },
              ...obj
            }
          }
        })
      } else {
        finalObj = data?.[USERS]?.map((usr: any) => {
          const obj = {
            [OCCUPANCY_TYPE]: usr[OCCUPANCY_TYPE],
            [PRIMARY_USER]: usr[PRIMARY_USER],
            [START_DATE]: usr[START_DATE] ? moment(usr[START_DATE]).format('YYYY-MM-DDT00:00:00') : null,
            [END_DATE]: usr[END_DATE] ? moment(usr[END_DATE]).format('YYYY-MM-DDT23:59:59') : null,
            [OWNERSHIP_SHARE]: usr[OCCUPANCY_TYPE] == OWNER ? usr[OWNERSHIP_SHARE] : ""
          }
  
          if (usr.userId) {
            return {
              [USER_ID]: usr[USER_ID],
              ...obj
            }
          } else {
            return {
              [NEW_USER]: {
                [USERNAME]: usr[USERNAME],
                [ALTERNATE_LOGIN_ID]: usr[ALTERNATE_LOGIN_ID],
                "title": "",
                [FIRST_NAME]: usr[FIRST_NAME],
                [LAST_NAME]: usr[LAST_NAME],
                [MOBILE_NO]: usr[MOBILE_NO],
                [LANDLINE_NO]: usr[LANDLINE_NO],
                [EMAIL_ID_1]: usr[EMAIL_ID_1],
                [EMAIL_ID_2]: usr[EMAIL_ID_2],
                [ADDRESS]: usr[ADDRESS],
                [PRIMARY_USER]: usr[PRIMARY_USER],
              },
              ...obj
            }
          }
        })
      }

      onSubmitBaseForm({
        [PROPERTY_ID]: ocpData?.[PROPERTY_ID]?.toString(),
        [OWNERSHIP_LIST]: finalObj,
        ...(data?.[ORGANIZATION_NAME] ? {[ORGANIZATION_NAME]: data?.[ORGANIZATION_NAME]} : {}),
        // [PROOF_DOCS]: formData[PROOF_DOCS],
        [OWNERSHIP_TYPE]: data?.[OWNERSHIP_TYPE],
        [MULTI_CORRESPOND]: data?.[MULTI_CORRESPOND],
        [CORRESPONDENCE_ADDRESS]: data?.[CORRESPONDENCE_ADDRESS],
        [CUSTOM_ADDRESS]: data?.[CUSTOM_ADDRESS] || null,
        [STATUS]: "OWNER_ONBOARDED",
      });
    }
  }

  const onSubmit = async (data: any) => {
    console.log("🚀 ~ onSubmit ~ data:", data)
    if (orgOwnershipType.includes((formData as any)[OWNERSHIP_TYPE]) && !data[ORGANIZATION_NAME]) {
      form.setError(ORGANIZATION_NAME, {message: "This Field is required"});
      return;
    } 

    const isOwnerLandlordExist = data[OWNERSHIP_LIST]?.find((usr: any) => usr?.[OCCUPANCY_TYPE] == OWNER || usr?.[OCCUPANCY_TYPE] == LANDLORD);

    if (!isOwnerLandlordExist) {
      toast.error("Please add at least one owner/landlord to continue.");
      return;
    }

    const proofDocs = form.getValues(PROOF_DOCS) || [];
    
    const isClosingDocValid = validateProofDoc(proofDocs);
    if (!isClosingDocValid) return;

    const docExist = proofDocs?.filter(
      (file: any) => file?.[PROOF_FILE] instanceof File
    ) || [];

    try {
      if (docExist.length > 0) {
        const result = await uploadClosingDoc(ocpData[OCP_ID], docExist);
        
        if (!result) {
          return;
        }
      }
  
      // 4. Proceeds to base form submission ONLY after files are uploaded
      baseFormSubmit(data);
    } catch (error) {
      console.error("Error uploading proof documents:", error);
      return; // Stop execution if document uploads fail
    }
  }

  const onChangePrimary = (idx: number) => {
    let usr = [...(formData as any)[USERS]];
    usr = usr.map((data: any) => ({...data, [PRIMARY_USER]: false}))
    usr[idx] = {...usr[idx], [PRIMARY_USER]: true}
    form.setValue(USERS, usr);
  }

  const ownerCount = useMemo(() => {
    return formData[USERS]?.filter((data: any) => data[OCCUPANCY_TYPE]?.toLowerCase() == "owner")?.length || 0
  }, [formData[USERS]]);

  const propertyName = useMemo(() => {
    return propertyList?.data?.find((data: any) => data?.propertyId?.toString() == formData[PROPERTY])?.name || ""
  }, [formData[PROPERTY]]);

  const ownershipShare = useMemo(() => {
    return formData[USERS]?.reduce(
      (acc: number, val: any) => {
        const num = parseFloat(val[OWNERSHIP_SHARE]);
        return acc + (isNaN(num) ? 0 : num);
      },
      0
    );
  }, [formData[USERS]]);

  const primaryContact = useMemo(() => {
    const row = formData[USERS]?.find((data: any) => data?.[PRIMARY_USER]);
    return row ? `${(row as any)[FIRST_NAME]} ${(row as any)[LAST_NAME]}` : ""
  }, [formData[USERS]]);

  const primaryUserDtl = useMemo(() => {
    const row = formData[USERS]?.find((data: any) => data?.[PRIMARY_USER]);
    return row || {};
  }, [formData[USERS]]);

  const primaryUserAddress = useMemo(() => {
    if (formData[CORRESPONDENCE_ADDRESS] == "property") {
      return ""
    } 
    if (formData[CORRESPONDENCE_ADDRESS] == "owner") {
      const row = formData[USERS]?.find((data: any) => data?.[PRIMARY_USER]);
      return (row as any)?.[ADDRESS] ? `${Object.values((row as any)[ADDRESS])?.join(", ")}` : "";
    }
    if (formData[CORRESPONDENCE_ADDRESS] == "custom") {
      return formData?.[CUSTOM_ADDRESS]?.[ADDRESS_1] ?`${Object.values(formData[CUSTOM_ADDRESS])?.join(", ")}` : "";
    }
  }, [formData[CORRESPONDENCE_ADDRESS], formData[CUSTOM_ADDRESS], formData[USERS]]);

  const onRemoveOwner = (usr: any) => {
    selectedUser.current = usr;
    setTimeout(() => {
      setOpenRemoveOwnerDialog(true);
    }, 200);
  }

  useEffect(() => {
    if (ocpData) {
      const typeList = proofTypeOptions?.filter((data: any) => data?.type == "userProof")?.map((data: any) => data?.value);

      const filteredDocs = ocpData?.proofDocList?.filter((doc: any) =>
        typeList.includes(doc?.entityType)
      );
      
      form.setValue(
        PROOF_DOCS,
        filteredDocs?.length
          ? filteredDocs?.map((data: any) => ({ [ATTACHMENT_ID]: data?.[ATTACHMENT_ID]?.toString(), [PROOF_FILE]: {name: data?.originalFileName || undefined}, [PROOF_TYPE]: data?.entityType }))
          : [{ [PROOF_FILE]: null, [PROOF_TYPE]: "" }]
      );
      setClosingKey(Math.random() * 3)
    }
  }, [ocpData]);

  const proofDocs = useWatch({ control: form.control, name: PROOF_DOCS }) || [];

  const onRemoveProof = (index: number) => () => {
    delete fileInputRefs.current[index];
    if ((proofDocs as any)?.[index]?.[PACKET_DOC_ID]) {
      deletePacketDocId.current.push((proofDocs as any)?.[index]?.[PACKET_DOC_ID])
    }
    form.setValue(
      PROOF_DOCS,
      proofDocs.filter((_: any, idx: number) => index !== idx)
    );
  };

  const onAddMore = () => {
    form.setValue(PROOF_DOCS, [
      ...proofDocs,
      { [PROOF_TYPE]: "", [PROOF_FILE]: null },
    ]);
  };

  const onDeleteConfirmIndividualUser = () => {
    let users = [...(formData as any)[USERS]];
    form.setValue(USERS, [...users.filter((usr: any) => usr.userId != selectedUser.current.userId)]);
    setOpenDeleteSingleOwnerDialog(false);
  }

  const onDeleteIndividualUser = (usr: any) => {
    selectedUser.current = usr;
    setOpenDeleteSingleOwnerDialog(true);
  }

  const onViewAttachment = (url: string) => {
    window.open(url, "_blank");
  };

  const {
    mutateAsync: addClosingProof,
    isPending: isAddDocPending,
  } = useAddOCPClosingDocumentMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
    },
  });

  const {
    mutateAsync: updateClosingProof,
    isPending: isUpdateDocPending,
  } = useUpdateOCPClosingDocumentMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
    },
  });

  const {
    mutateAsync: deleteClosingProof,
    isPending: isDeleteDocPending,
  } = useDeleteOCPClosingDocumentMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
    },
  });

  const validateProofDoc = (data: any): boolean => {
    const docs = data[PROOF_DOCS] || [];

    for (const doc of docs) {
      const isRequired = Boolean(doc?.[IS_REQUIRED]);
      const hasFile = Boolean(doc?.[PROOF_FILE]);
      const hasType = Boolean(doc?.[PROOF_TYPE]);

      // Rule 1: If doc is required and proof file is missing -> Error
      if (isRequired && !hasFile) {
        toast.error("Please upload the required proof file.");
        return false;
      }

      // Rule 2: If optional (not required), type is selected, but file is missing -> Error
      // (If both type and file are empty, it passes safely as valid optional row)
      if (!isRequired && hasType && !hasFile) {
        toast.error("Please upload a proof file for the selected proof type.");
        return false;
      }
    }

    return true;
  };

  const uploadClosingDoc = async (ocpId: string, docsToUpload: any[]) => {
    const docExist = docsToUpload?.filter(
      (file: any) => file?.[PROOF_FILE] instanceof File
    ) || [];

    let uploadPromises: any[] = [];

    if (deletePacketDocId.current?.length) {
      deletePacketDocId.current?.forEach((packageDocId: string) => {
        uploadPromises.push(
          deleteClosingProof({
            ocpId: ocpId.toString(),
            packageDocId
          })
        )
      })
      
    }

    if (docExist.length) {
      docExist.map((file: any) => {
        const formData = new FormData();
        formData.append("file", file?.[PROOF_FILE]);
        formData.append("ocpStage", "OWNER_ONBOARDING");
        formData.append("documentTypeId", file?.[PROOF_TYPE]);
  
        if (file[PACKET_DOC_ID]) {
          uploadPromises.push(updateClosingProof({
            ocpId: ocpId.toString(),
            packageDocId: file?.[PACKET_DOC_ID],
            data: formData,
          }));
        } else {
          uploadPromises.push(addClosingProof({
            ocpId: ocpId.toString(),
            data: formData,
          }));
        }
      });
  
      try {
        // 1. Wait for all mutation promises to resolve
        const results = await Promise.all(uploadPromises);
  
        // 2. Extract success payload data from response
        const newlyUploadedFiles = results.map((res: any, index: number) => ({
          ...res?.data, // API response data (e.g., attachmentId, filePath, etc.)
          proofType: docExist[index]?.[PROOF_TYPE],
          fileName: docExist[index]?.[PROOF_FILE]?.name,
        }));
  
        // 3. Update React state (append to existing state)
        dispatch(setOCPData({ ...ocpData, [PROOF_DOCS]: [...newlyUploadedFiles] }))
  
        return true;
      } catch (error) {
        console.error("One or more files failed to upload:", error);
        throw error;
      }
    }
  };
    
  return {
    form,
    isCommunityLoading,
    communityList: communityList?.data || [],
    propertyList: propertyList?.data || [],
    isPropertyLoading,
    onOwnershipTypeChange,
    formData,
    onAddUser,
    requiredFields,
    onSave,
    openAddEditDialog,
    setOpenAddEditDialog,
    isMobile,
    onEditUser,
    selectedUser: selectedUser.current,
    action: dialogAction.current,
    onAddressTypeChange,
    addUserDisabled,
    onChangePrimary,
    ownerCount,
    communityData,
    isView,
    propertyName,
    ownershipShare,
    primaryContact,
    isEdit,
    primaryUserAddress,
    primaryUserDtl,
    openRemoveOwnerDialog,
    setOpenRemoveOwnerDialog,
    onRemoveOwner,
    onRemoveSave,
    isStateLoading,
    stateList: stateList?.data || [],
    keys,
    onSubmit,
    isOwnershipDtlLoading,
    onRemoveProof,
    onAddMore,
    proofDocs,
    fileInputRefs,
    closingKey,
    onDeleteConfirmIndividualUser,
    onDeleteIndividualUser,
    openDeleteSingleOwnerDialog,
    setOpenDeleteSingleOwnerDialog,
    isDocListLoading: isDocListLoading || isLoadingProofList,
    updatedKey,
    ocpDocList: ocpDocList?.data || [],
    onViewAttachment,
    isUpdateDocPending: isAddDocPending || isDeleteDocPending || isUpdateDocPending
  }
}

export default useStep5;